import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { TrophyGlyph } from '../components/icons/BeelyvIcons';
import { ActivityContext } from '../components/ActivityContext';
import { Avatar } from '../components/Avatar';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { PulseRail } from '../components/PulseRail';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { findByUsername } from '../data/people';
import { useLocale } from '../contexts/LocaleContext';
import { useSocial } from '../contexts/SocialContext';
type Tab = 'posts' | 'pulses';
export function PublicProfile() {
  const {
    username = ''
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const [tab, setTab] = useState<Tab>('posts');
  const person = findByUsername(username);
  if (!person) {
    return <div className="px-2 pb-2" data-id="element-3157" data-mp="383r3uqp07uyc">
        <PageHeader title={t('profile.title')} showBack data-id="element-3158" data-mp="382ockhnrrqhw" />
        <EmptyState title={t('profile.missing.title')} body={t('profile.missing.body')} data-id="element-3159" data-mp="2u4hvueayc75g" />
      </div>;
  }
  const following = social.isFollowing(person.id);
  const personPosts = social.posts.filter(post => post.authorId === person.id);
  const personPulses = social.pulses.filter(ring => ring.authorId === person.id);
  return <div className="px-2 pb-2" data-id="element-3160" data-mp="2p5wvmevp9nh0">
      <PageHeader title={person.name} showBack data-id="element-3161" data-mp="2p780ki0vt19p" />

      <Card className="p-2" data-id="element-3162" data-mp="2ah0eztfkv24s">
        <div className="flex items-start gap-2" data-id="element-3163" data-mp="2ahtp94dqw9ro">
          <Avatar src={person.avatar} name={person.name} size={68} ring={personPulses.length > 0 ? personPulses[0].seen ? 'seen' : 'unseen' : 'none'} onClick={personPulses.length > 0 ? () => navigate(`/pulse/${personPulses[0].id}`) : undefined} label={`${t('pulse.open')} — ${person.name}`} data-id="element-3164" data-mp="2ff26n2r2k9rv" />
          <div className="min-w-0 flex-1" data-id="element-3165" data-mp="76x387ixdsvw">
            <p className="bee-overline text-[9px] font-bold text-gold-deep" data-id="element-3166" data-mp="28ls7tgw9cc3">
              {t(`category.${person.category}`)} ·{' '}
              {t('post.streak', {
              n: person.streak
            })}
            </p>
            <p className="mt-[2px] truncate text-[17px] font-bold text-deep" data-id="element-3167" data-mp="uxauvwxllppy">
              {person.name}
            </p>
            <p className="truncate text-[12px] text-muted" data-id="element-3168" data-mp="pxoghk61a3fk">{person.username}</p>
          </div>
        </div>

        <p className="mt-2 text-[13px] leading-relaxed text-deep" data-id="element-3169" data-mp="lvtgqk3r3y3w">{tl(person.bio)}</p>

        <dl className="mt-2 flex items-center gap-3" data-id="element-3170" data-mp="gy1hf50s19po">
          {[{
          key: 'profile.posts',
          value: person.counts.posts
        }, {
          key: 'profile.followers',
          value: social.followerCount(person.id)
        }, {
          key: 'profile.followingCount',
          value: person.counts.following
        }].map(item => <div key={item.key} data-id="element-3171" data-mp="1h6zs7cpwnagv">
              <dd className="bee-num text-[16px] font-bold leading-none text-deep" data-id="element-3172" data-mp="1h3ls6b4prdcl">
                {item.value}
              </dd>
              <dt className="mt-[2px] text-[10.5px] text-muted" data-id="element-3173" data-mp="1q5uz6r0rwol8">{t(item.key)}</dt>
            </div>)}
        </dl>

        <Button variant={following ? 'outline' : 'primary'} size="lg" fullWidth className="mt-2" onClick={() => social.toggleFollow(person.id)} data-id="element-3174" data-mp="1uy4vcoshekvy">
          {following ? t('profile.following') : t('profile.follow')}
        </Button>
      </Card>

      <section className="mt-3" data-id="element-3175" data-mp="29vf3bo5x18mx">
        <SectionTitle data-id="element-3176" data-mp="29s9ema1788rq">{t('profile.achievements')}</SectionTitle>
        <ul className="flex flex-wrap gap-1" data-id="element-3177" data-mp="2duxn2gm03do6">
          {person.badges.map(badge => <li key={badge} className="flex min-h-[38px] items-center gap-1 rounded-pill border border-gold/45 bg-white/70 px-2 text-[12px] font-medium text-deep" data-id="element-3178" data-mp="fhe8rfhef7sn">
              <TrophyGlyph size={16} className="text-gold-deep" aria-hidden="true" data-id="element-3179" data-mp="3kc45ypj4p5cj" />
              {t(badge)}
            </li>)}
        </ul>
      </section>

      <section className="mt-3" data-id="element-3180" data-mp="3ub36tlxnjwoz">
        <SectionTitle data-id="element-3181" data-mp="3ua0i9knsy6c8">{t('profile.publicMetrics')}</SectionTitle>
        <div className="grid grid-cols-2 gap-2" data-id="element-3182" data-mp="3pci8xt5vhp3p">
          {person.publicMetrics.map(metric => <MetricCard key={metric.value + tl(metric.label)} label={tl(metric.label)} value={metric.value} data-id="element-3183" data-mp="31med47yxetn9" />)}
        </div>
        <p className="mt-1 flex items-center gap-1 text-[11px] text-muted" data-id="element-3184" data-mp="3bch8ufxfmlpa">
          <LockIcon size={12} aria-hidden="true" data-id="element-3185" data-mp="3bek6nm4ttj4k" />
          {t('profile.privateNote')}
        </p>
      </section>

      <section className="mt-3" data-id="element-3186" data-mp="2iw6hsc1gx7hm">
        <div className="flex gap-1 rounded-pill bg-sand-deep/70 p-[3px]" role="tablist" aria-label={t('profile.title')} data-id="element-3187" data-mp="2ivenr5nirhtg">
          {(['posts', 'pulses'] as Tab[]).map(item => <button key={item} type="button" role="tab" aria-selected={tab === item} onClick={() => setTab(item)} className={`min-h-[36px] flex-1 rounded-pill text-[12px] font-medium transition-colors duration-150 ease-brand ${tab === item ? 'bg-deep text-sand' : 'text-muted hover:text-deep'}`} data-id="element-3188" data-mp="tdhp0wyq3zfs">
              {t(item === 'posts' ? 'profile.posts' : 'pulse.title')}
            </button>)}
        </div>

        <div className="mt-2" data-id="element-3189" data-mp="134ufpl8scjk8">
          {tab === 'posts' ? personPosts.length === 0 ? <EmptyState title={t('profile.noPosts.title')} body={t('profile.noPosts.body')} data-id="element-3190" data-mp="183oxcjv0o22r" /> : <ul className="grid grid-cols-2 gap-2" data-id="element-3191" data-mp="3nujrtqi3tr98">
                {personPosts.map(post => <li key={post.id} data-id="element-3192" data-mp="3sug041xu44ul">
                    <button type="button" onClick={() => navigate(`/post/${post.id}`)} className="block w-full text-left" data-id="element-3193" data-mp="3svhbk7iritcu">
                      <img src={post.media} alt="" aria-hidden="true" className="aspect-square w-full rounded-[12px] object-cover" data-id="element-3194" data-mp="1tgxvhk0un21w" />
                      <span className="mt-1 block" data-id="element-3195" data-mp="1fnq2pp1180im">
                        <ActivityContext context={post.context} data-id="element-3196" data-mp="1fldfoxgf2zyp" />
                      </span>
                      <span className="mt-[2px] line-clamp-2 block text-[11.5px] leading-snug text-deep" data-id="element-3197" data-mp="1aqg5vobbcmkr">
                        {tl(post.caption)}
                      </span>
                    </button>
                  </li>)}
              </ul> : personPulses.length === 0 ? <EmptyState title={t('pulse.empty.title')} body={t('pulse.empty.body')} data-id="element-3198" data-mp="2w1pzws8htc26" /> : <PulseRail pulses={personPulses} data-id="element-3199" data-mp="htyk58mn6ey1" />}
        </div>
      </section>
    </div>;
}